<script setup>
import ArrowSquareOutIcon from '@iconify-vue/ph/arrow-square-out';
import { Link } from '@inertiajs/vue3';
import NavBadge from '@js/components/navigation/NavBadge.vue';

const {
    title,
    icon = null,
    href,
    external = false,
    callback,
    active = false,
    level = 0,
    type = 'default',
    disclosure,
    setCurrentDisclosure,
    pinValue = undefined,
    pingType = 'primary',
    animatePing = false,
    hideTextOnMobile = false,
} = defineProps({
    title: {
        type: String,
        required: true,
    },
    icon: {
        type: [Object, Function],
        required: false,
    },
    href: {
        type: String,
        required: false,
    },
    external: {
        type: Boolean,
        required: false,
        default: false,
    },
    callback: {
        type: Function,
        required: false,
    },
    active: {
        type: Boolean,
        required: false,
    },
    level: {
        type: Number,
        required: false,
    },
    type: {
        type: String,
        required: false,
    },
    disclosure: {
        required: false,
    },
    setCurrentDisclosure: {
        type: Function,
        required: false,
    },
    pinValue: {
        type: [Number, String],
        required: false,
    },
    pingType: {
        type: String,
        required: false,
    },
    animatePing: {
        type: Boolean,
        required: false,
        default: false,
    },
    hideTextOnMobile: {
        type: Boolean,
        required: false,
        default: false,
    },
});

function toggleButton() {
    if (callback) {
        callback();
    } else if (disclosure && setCurrentDisclosure) {
        setCurrentDisclosure(disclosure);
    }
}
</script>

<template>
    <component
        v-if="href || callback"
        :is="href ? (external ? 'a' : Link) : 'button'"
        :type="href ? (external ? undefined : 'button') : undefined"
        :href="href ?? undefined"
        :target="external ? '_blank' : undefined"
        :rel="external ? 'noopener noreferrer' : undefined"
        class="group relative flex shrink-0 items-center gap-2 truncate rounded-lg p-2 duration-150 hover:bg-indigo-600 hover:text-white"
        :class="{
            'bg-indigo-600 text-white': active,
            'text-amber-500 hover:bg-amber-400': type === 'warning',
            'bg-amber-400 !text-white': type === 'warning' && active,
            '!overflow-visible': pinValue !== undefined,
        }"
        @click="toggleButton"
    >
        <component
            v-if="icon && level === 0"
            :is="icon"
            class="inline-block size-5 group-hover:text-white"
            :class="{
                'text-gray-400': type === 'default',
                'text-amber-400 group-hover:text-white': type === 'warning',
                'text-white': active,
                '!text-white': type === 'warning' && active,
            }"
        />

        <div
            :class="{
                'pl-7': level === 1,
                'pl-12': level === 2,
                'max-xl:hidden': hideTextOnMobile,
            }"
        >
            {{ title }}
        </div>

        <ArrowSquareOutIcon
            v-if="external"
            class="inline-block size-4 shrink-0 text-gray-400 group-hover:text-white"
            :class="{ 'text-white': active }"
        />

        <NavBadge
            :pin-value="pinValue"
            :ping-type="pingType"
            :animate-ping="animatePing"
        />
    </component>

    <div
        v-else
        class="group flex shrink-0 cursor-not-allowed items-center gap-2 truncate rounded-lg p-2 opacity-50 duration-150"
        :class="{
            'bg-indigo-600 text-white': active,
            'text-amber-500 hover:bg-amber-400': type === 'warning',
            'bg-amber-400 !text-white': type === 'warning' && active,
        }"
    >
        <component
            v-if="icon && level === 0"
            :is="icon"
            class="inline-block size-5 group-hover:text-white"
            :class="{
                'text-gray-400': type === 'default',
                'text-amber-400 group-hover:text-white': type === 'warning',
                'text-white': active,
                '!text-white': type === 'warning' && active,
            }"
        />

        <div :class="{ 'pl-7': level === 1, 'pl-12': level === 2 }">
            {{ title }}
        </div>
    </div>
</template>
